import { gql } from '@apollo/client';
import { andList } from './pageChecks';

/** How much of the archive Prism AI has read: the header line and Write's warning. */
export const PRISM_COVERAGE = gql`
  query PrismCoverage($accountId: ID!) {
    researchOverview(accountId: $accountId, limit: 1) {
      articleCount
      atomizedCount
      pageCount
      pagesRead
      pagesSkipped
      articlesTooShort
      autoRead
      factCount
    }
  }
`;

/** What researchOverview says about how much Prism AI has read. */
export interface PrismCoverage {
  articleCount: number;
  atomizedCount: number;
  pageCount?: number;
  pagesRead?: number;
  /** Live pages left alone on purpose (kept out of search); older servers omit it. */
  pagesSkipped?: string[];
  /** Unread live articles with no body text beyond their summary. */
  articlesTooShort?: number;
  /** New articles are read automatically (the Prism AI switch). */
  autoRead?: boolean;
  factCount: number;
}

const n = (v: number) => v.toLocaleString('en-US');

/**
 * The Prism AI header line (Prototype v15): "12 of 412 articles and 3 of 4
 * pages read by Prism AI · 152 facts", then why anything isn't read.
 *
 * Pages count every live page, so the total matches Content › Pages; one
 * kept out of search isn't read, and says so ("7 of 8 pages · Search isn't
 * read", UX review round 24). Unread articles say why: waiting for the next
 * read, too short to read, or reading is off (round 22: "0 of 1 article read"
 * two days on, with nothing saying why). A site with pages and no articles
 * reads "8 of 8 pages read by Prism AI", no "0 of 0 articles and" (60b346c4).
 */
export function prismCoverageWords(cov: PrismCoverage, facts = false): string {
  const skipped = cov.pagesSkipped || [];
  const pageTotal = (cov.pageCount || 0) + skipped.length;
  const pages = pageTotal
    ? `${n(cov.pagesRead || 0)} of ${n(pageTotal)} ${pageTotal === 1 ? 'page' : 'pages'}`
    : '';
  const articles =
    cov.articleCount || !pages
      ? `${n(cov.atomizedCount)} of ${n(cov.articleCount)} ${
          cov.articleCount === 1 ? 'article' : 'articles'
        }`
      : '';
  const unread = Math.max(cov.articleCount - cov.atomizedCount, 0);
  const short = Math.min(cov.articlesTooShort || 0, unread);
  const waiting = unread - short;
  const notes = [
    facts ? `${n(cov.factCount)} ${cov.factCount === 1 ? 'fact' : 'facts'}` : '',
    waiting
      ? cov.autoRead === false
        ? `${n(waiting)} not read: reading new articles is off`
        : `${n(waiting)} waiting to be read`
      : '',
    short ? `${n(short)} too short to read` : '',
    skipped.length
      ? `${andList(skipped)} ${skipped.length === 1 ? 'isn’t' : 'aren’t'} read (kept out of search)`
      : '',
  ].filter(Boolean);
  return [`${[articles, pages].filter(Boolean).join(' and ')} read by Prism AI`, ...notes].join(
    ' · '
  );
}

/**
 * Write's warning when Prism AI has read no articles yet: drafts are built
 * only from read articles, so there's nothing to assemble from. Null once one
 * is read. Says why, as the header does.
 */
export function nothingReadWords(cov: PrismCoverage): string | null {
  if (cov.atomizedCount > 0) return null;
  if (!cov.articleCount)
    return 'Write drafts from articles Prism AI has read, and this site has none yet. Publish an article first.';
  const short = Math.min(cov.articlesTooShort || 0, cov.articleCount);
  const why =
    short === cov.articleCount
      ? cov.articleCount === 1
        ? 'Its one article is too short to read.'
        : 'Its articles are too short to read.'
      : cov.autoRead === false
        ? 'Reading new articles is off for this site.'
        : `${n(cov.articleCount - short)} ${
            cov.articleCount - short === 1 ? 'article is' : 'articles are'
          } waiting to be read.`;
  return `Prism AI hasn’t read any articles yet, so there’s nothing to draft from. ${why}`;
}
